// src/components/CarrierSenderPanel.js - RULING 305.13 (Record 654; ruling 305.8). Who sends this claim to the carrier, for
// every party; and, for the General Chairperson of its committee, her written assignment of a named person (or its
// withdrawal). The people offered are her committee's published seats (committees/{c}.seats, as AssignmentsPanel uses)
// and herself. Decisions: src\data\carrierSender.js. Who may write: firestore.rules.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { claimPatch } from '../data/claimWrite';
import { whoSends, mayAssignSender, assignPatch, withdrawPatch } from '../data/carrierSender';

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export default function CarrierSenderPanel({ claimId, claim, user, onPatched }) {
  const [seats, setSeats] = useState([]);
  const [pick, setPick] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // Forensic audit 2026-10-16: a failed read of the seats used to become [] - "No seat with an account is published".
  // The failure is now said for what it is; she can still name herself (that needs no read).
  const [seatsErr, setSeatsErr] = useState('');
  const may = mayAssignSender(user, claim);
  const committee = String((claim && (claim.committee || claim.generalCommittee)) || '');
  useEffect(() => {
    if (!may || !committee) return;
    let dead = false;
    setSeatsErr('');
    getDoc(doc(db, 'committees', committee)).then((s) => {
      if (dead) return;
      const list = (s.exists() && Array.isArray(s.data().seats)) ? s.data().seats.filter((x) => x && x.uid && ['local_chair', 'general_chair'].includes(x.role)) : [];
      setSeats(list);
    }).catch((e) => { if (!dead) { setSeats([]); setSeatsErr('The committee\'s seats could not be read (' + ((e && (e.code || e.message)) || 'refused') + '), so only you are offered. This is NOT "no seat published" - reload the page to name someone else.'); } });
    return () => { dead = true; };
  }, [may, committee]);
  if (!claim || String(claim.status || '') === 'draft') return null;
  const w = whoSends(claim);
  const people = [{ uid: user && user.uid, name: (user && (user.name || user.email)) + ' (you)', role: 'general_chair' }]
    .filter((p) => may && p.uid && user.role === 'general_chair')
    .concat(seats.map((x) => ({ uid: x.uid, name: x.name + (x.scopeKey ? ' (' + x.scopeKey + ')' : ''), role: x.role })));
  const write = async (r) => {
    if (!r.ok) { setMsg(r.errors.join(' ')); return; }
    setBusy(true); setMsg('');
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({ ...r.patch, updatedAt: serverTimestamp() }, user));
      setMsg('Recorded.'); setPick(''); setNote('');
      if (onPatched) onPatched(r.patch);
    } catch (e) { setMsg('NOT recorded: ' + ((e && (e.code || e.message)) || 'refused') + '.'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ border: '1px solid ' + C.line, borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: C.dim, lineHeight: 1.5 }}>
      <div style={{ color: C.text }}>{w.line}</div>
      {claim.carrierSender && claim.carrierSender.note ? <div style={{ fontStyle: 'italic', marginTop: 2 }}>"{claim.carrierSender.note}"</div> : null}
      {may ? (
        <div style={{ marginTop: 8 }}>
          <select aria-label="Assign in writing - who sends it" className="form-select" value={pick} onChange={(e) => setPick(e.target.value)} style={{ fontSize: 12, marginRight: 8 }}>
            <option value="">Assign in writing - who sends it…</option>
            {people.map((p) => <option key={p.uid} value={p.uid}>{p.name}</option>)}
          </select>
          <input aria-label="Note (optional)" className="form-input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={{ fontSize: 12, width: 220, marginRight: 8 }} />
          <button className="btn btn-primary btn-sm" disabled={busy || !pick}
            onClick={() => write(assignPatch(claim, user, people.find((p) => p.uid === pick), note, serverTimestamp()))} style={{ marginRight: 8 }}>Assign</button>
          {claim.carrierSender ? <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => write(withdrawPatch(claim, user))}>Withdraw the assignment</button> : null}
          {seatsErr ? <div style={{ marginTop: 4, color: C.bad }}>&#9888; {seatsErr}</div> : null}
          {people.length === 0 && !seatsErr ? <div style={{ marginTop: 4 }}>No seat with an account is published for {committee} yet, so there is nobody to name.</div> : null}
        </div>
      ) : null}
      {msg ? <div style={{ marginTop: 6, color: /^NOT|^Only|^Name|^Assign|^Keep|^There/.test(msg) ? C.bad : C.ok }}>{msg}</div> : null}
    </div>
  );
}
